import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Award, Plus, Search, X, Filter, ChevronDown, ChevronUp,
  Download, Upload, FileText, FileSpreadsheet, FileOutput, List, LayoutList,
  Layers, CheckCircle, XCircle, Globe, Mail, Phone, Package, Factory, Tag,
  Clock, RefreshCcw,
  LayoutGrid, Shield, MapPin
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import { getBrandCountryOptions, getBrandDefaultCountry } from '../../shared/utils/brandCountry';
import { useTranslation } from 'react-i18next';
import {sliceQawaemStats,
  mapStatusOptionsToColumnFilter,
  QAWAEM_KPI_GRID_CLASS,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  createArticleStatusChangeHandler,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const NS = 'modules.article.pages.marquesFabricantsPage';

const EMPTY_FORM = {
  entity_number: '',
  name: '',
  entity_type: '',
  country: '',
  website: '',
  contact_email: '',
  contact_phone: '',
  product_count: '',
  status: '',
  notes: ''
};

const MarquesFabricantsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const articlesConfig = useArticlesConfig();

  const countryOptions = useMemo(
    () => getBrandCountryOptions((key) => p(key)),
    [t],
  );
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_entities');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'العلامة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'علامة' });

  const [formTab, setFormTab] = useState('general');

  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM, country: getBrandDefaultCountry() }));

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => { loadEntities(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/articles/marques-fabricants');
      const rows = response?.data || response || [];
      setEntities(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading marques/fabricants:', error);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/articles/marques-fabricants', loadEntities),
    [],
  );

  const stats = {
    total: entities.length,
    marques: entities.filter(e => e.entity_type === 'marque').length,
    fabricants: entities.filter(e => e.entity_type === 'fabricant').length,
    actives: entities.filter(e => e.status === 'actif').length,
    produits: entities.reduce((sum, e) => sum + (parseInt(e.product_count, 10) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_entities', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_type', name: t('common.by_type'), icon: Tag },
    { id: 'par_statut', name: t('common.by_status'), icon: Shield },
    { id: 'par_pays', name: p('tab_by_country'), icon: MapPin }
  ];

  const quickFilterButtons = [
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'marque', name: p('quick_marques'), icon: Award },
    { id: 'fabricant', name: p('quick_fabricants'), icon: Factory },
    { id: 'actif', name: p('quick_actifs'), icon: CheckCircle },
    { id: 'inactif', name: p('quick_inactifs'), icon: XCircle }
  ];

  const getCountryDistribution = () => {
    const counts = {};
    entities.forEach(e => {
      const country = e.country || p('not_specified');
      counts[country] = (counts[country] || 0) + 1;
    });
    const colors = [
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-green-500 to-green-600',
      'bg-gradient-to-r from-purple-500 to-purple-600',
      'bg-gradient-to-r from-amber-500 to-amber-600',
      'bg-gradient-to-r from-red-500 to-red-600'
    ];
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }));
  };

  const entityDistribution = {
    all_entities: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'marques', name: p('dist_marques'), count: stats.marques, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'fabricants', name: p('dist_fabricants'), count: stats.fabricants, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'actifs', name: p('dist_actifs'), count: stats.actives, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'inactifs', name: p('dist_inactifs'), count: stats.total - stats.actives, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'marques', name: p('dist_marques'), count: stats.marques, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'fabricants', name: p('dist_fabricants'), count: stats.fabricants, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'actifs', name: p('dist_actifs'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactifs', name: p('dist_inactifs'), count: stats.total - stats.actives, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_pays: {
      title: p('dist_by_country'),
      data: getCountryDistribution()
    }
  };

  const distributionInfo = entityDistribution[mainViewMode] || entityDistribution.all_entities;

  const baseFilteredEntities = useMemo(
    () =>
      entities.filter((entity) => {
        if (statusFilter !== 'all' && entity.status !== statusFilter) return false;

        if (dateRange.start || dateRange.end) {
          const createdDate = new Date(entity.created_at);
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (createdDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (createdDate > endOfDay) return false;
          }
        }

        if (quickFilter === 'marque' && entity.entity_type !== 'marque') return false;
        if (quickFilter === 'fabricant' && entity.entity_type !== 'fabricant') return false;
        if (quickFilter === 'actif' && entity.status !== 'actif') return false;
        if (quickFilter === 'inactif' && entity.status !== 'inactif') return false;

        if (activeDistributionFilter) {
          if (mainViewMode === 'par_type' || mainViewMode === 'all_entities') {
            if (activeDistributionFilter === 'marques' && entity.entity_type !== 'marque') return false;
            if (activeDistributionFilter === 'fabricants' && entity.entity_type !== 'fabricant') return false;
          }
          if (mainViewMode === 'par_statut' || mainViewMode === 'all_entities') {
            if (activeDistributionFilter === 'actifs' && entity.status !== 'actif') return false;
            if (activeDistributionFilter === 'inactifs' && entity.status !== 'inactif') return false;
          }
          if (mainViewMode === 'par_pays') {
            const country = entity.country || p('not_specified');
            if (country !== activeDistributionFilter) return false;
          }
        }

        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          const matches =
            entity.entity_number?.toLowerCase().includes(term) ||
            entity.name?.toLowerCase().includes(term) ||
            entity.country?.toLowerCase().includes(term) ||
            entity.website?.toLowerCase().includes(term) ||
            entity.contact_email?.toLowerCase().includes(term);
          if (!matches) return false;
        }

        return true;
      }),
    [entities, statusFilter, dateRange, quickFilter, activeDistributionFilter, mainViewMode, searchTerm, p],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('entity_number', (r) => r.entity_number || ''),
      textColumnRule('created_at', (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString(dateLocale) : '',
      ),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('entity_type', (r) =>
        r.entity_type === 'fabricant' ? p('type_fabricant') : p('type_marque'),
      ),
      textColumnRule('country', (r) => r.country || ''),
      textColumnRule('website', (r) => r.website || ''),
      textColumnRule('product_count', (r) => String(parseInt(r.product_count, 10) || 0)),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [dateLocale, p],
  );

  const filteredEntities = useMemo(
    () => applyColumnFilters(baseFilteredEntities, colFilters, columnFilterRules),
    [baseFilteredEntities, colFilters, columnFilterRules],
  );

  const filteredEntityIds = useMemo(() => filteredEntities.map((e) => e.id), [filteredEntities]);
  const displayedEntities = useMemo(
    () => sliceArticlesListItems(filteredEntities, articlesConfig.itemsPerPage),
    [filteredEntities, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredEntityIds.length > 0 && filteredEntityIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredEntityIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(selectedRecords.includes(id)
      ? selectedRecords.filter(rId => rId !== id)
      : [...selectedRecords, id]
    );
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      window.alert(p('name_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        product_count: parseInt(formData.product_count, 10) || 0
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/articles/marques-fabricants/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/articles/marques-fabricants', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadEntities();
      resetForm();
    } catch (error) {
      console.error('Error saving entry:', error);
      notifyError(showNotify, p('save_error', { defaultValue: 'تعذّر الحفظ' }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      entity_number: record.entity_number ?? '',
      name: record.name ?? '',
      entity_type: record.entity_type ?? '',
      country: record.country ?? '',
      website: record.website ?? '',
      contact_email: record.contact_email ?? '',
      contact_phone: record.contact_phone ?? '',
      product_count: record.product_count ?? '',
      status: record.status ?? '',
      notes: record.notes ?? ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`/articles/marques-fabricants/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        await loadEntities();
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map(id => localApiAdapter.delete(`/articles/marques-fabricants/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        loadEntities();
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
        notifyError(showNotify, p('save_error', { defaultValue: 'تعذّر الحذف' }));
      } finally {
        setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, country: getBrandDefaultCountry() });
    setSelectedRecord(null);
    setShowModal(false);
    setFormTab('general');
  };

  const openNewModal = () => {
    setFormData({ ...EMPTY_FORM, country: getBrandDefaultCountry() });
    setSelectedRecord(null);
    setFormTab('general');
    setShowModal(true);
  };

  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_general'), icon: Award },
    { id: 'products_notes', label: p('section_products_notes'), icon: Package },
  ]), [p]);

  const getTypeBadge = (type) => {
    if (type === 'fabricant') {
      return (
        <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-green-500/20 text-green-400 border-green-500/30">
          <Factory size={14} />{p('type_fabricant')}
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30">
        <Award size={14} />{p('type_marque')}
      </span>
    );
  };

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: t('common.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'inactif', label: t('common.inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [t],
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const getStatusBadge = (status) => {
    if (status === 'inactif') {
      return (
        <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-red-500/20 text-red-400 border-red-500/30">
          <XCircle size={14} />{t('common.inactive')}
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-green-500/20 text-green-400 border-green-500/30">
        <CheckCircle size={14} />{t('common.active')}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  };

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'entity_number', 'created_at', 'name', 'entity_type', 'country', 'website', 'product_count', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'entity_number', label: p('col_number'), checked: columnVisibility.entity_number !== false, required: true },
      { key: 'created_at', label: p('col_date'), checked: columnVisibility.created_at !== false },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'entity_type', label: p('col_type'), checked: columnVisibility.entity_type !== false },
      { key: 'country', label: p('col_country'), checked: columnVisibility.country !== false },
      { key: 'website', label: p('col_website'), checked: columnVisibility.website !== false },
      { key: 'product_count', label: p('col_products'), checked: columnVisibility.product_count !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className="space-y-6 employees-container">
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Award className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadEntities} className="refresh-button" title={t('common.refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openNewModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_entry')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards — 5 glow stats */}
      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { label: t('common.total'), value: stats.total, icon: Layers, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_marques'), value: stats.marques, icon: Award, color: 'from-purple-500 to-purple-600' },
          { label: p('stat_fabricants'), value: stats.fabricants, icon: Factory, color: 'from-green-500 to-green-600' },
          { label: p('stat_actives'), value: stats.actives, icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' },
          { label: p('stat_linked_products'), value: stats.produits, icon: Package, color: 'from-amber-500 to-amber-600' }
        ], 4).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 overflow-hidden"
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {mainViewMode === 'all_entities' && (
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {quickFilterButtons.map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setQuickFilter(filter.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                    >
                      <filter.icon className="h-5 w-5" />
                      <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
                {activeDistributionFilter && (
                  <button
                    onClick={() => setActiveDistributionFilter(null)}
                    className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                  >
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
                {distributionInfo.data.map(item => {
                  const filterKey = item.filterKey || item.name;
                  return (
                  <button
                    key={filterKey}
                    onClick={() => setActiveDistributionFilter(activeDistributionFilter === filterKey ? null : filterKey)}
                    className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-medium ${activeDistributionFilter === filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                      <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                      <div
                        className={`h-3 rounded-full ${item.color} transition-all`}
                        style={{
                          width: `${(() => {
                            const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                            return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                          })()}%`
                        }}
                      />
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('entity_number') ? <TableColumnHeaderFilter columnKey="entity_number" label={p('col_number')} value={colFilters.entity_number ?? ''} onChange={(v) => setColFilter('entity_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('created_at') ? <TableColumnHeaderFilter columnKey="created_at" label={p('col_date')} value={colFilters.created_at ?? ''} onChange={(v) => setColFilter('created_at', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('entity_type') ? <TableColumnHeaderFilter columnKey="entity_type" label={p('col_type')} value={colFilters.entity_type ?? ''} onChange={(v) => setColFilter('entity_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('country') ? <TableColumnHeaderFilter columnKey="country" label={p('col_country')} value={colFilters.country ?? ''} onChange={(v) => setColFilter('country', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('website') ? <TableColumnHeaderFilter columnKey="website" label={p('col_website')} value={colFilters.website ?? ''} onChange={(v) => setColFilter('website', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('product_count') ? <TableColumnHeaderFilter columnKey="product_count" label={p('col_products')} value={colFilters.product_count ?? ''} onChange={(v) => setColFilter('product_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{t('common.loading')}</td></tr>
                ) : displayedEntities.length > 0 ? (
                  displayedEntities.map((entity, index) => (
                    <tr
                      key={entity.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(entity.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(entity.id)} onChange={() => handleSelectRecord(entity.id)} />
                      {showCol('entity_number') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold font-mono">{entity.entity_number || 'N/A'}</span></td>
                      ) : null}
                      {showCol('created_at') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {formatDate(entity.created_at)}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">{formatTime(entity.created_at)}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{entity.name || 'N/A'}</span>
                            {entity.contact_email && (
                              <span className="text-white/50 text-xs mt-0.5">{entity.contact_email}</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {showCol('entity_type') ? (
                        <td className="px-6 py-4">{getTypeBadge(entity.entity_type)}</td>
                      ) : null}
                      {showCol('country') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{entity.country || '—'}</span></td>
                      ) : null}
                      {showCol('website') ? (
                        <td className="px-6 py-4">
                          {entity.website ? (
                            <a href={entity.website.startsWith('http') ? entity.website : `https://${entity.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                              <Globe size={14} />{entity.website.replace(/^https?:\/\//, '').slice(0, 24)}
                            </a>
                          ) : (
                            <span className="text-white/40 text-sm">—</span>
                          )}
                        </td>
                      ) : null}
                      {showCol('product_count') ? (
                        <td className="px-6 py-4"><span className="text-white font-bold">{entity.product_count || 0}</span></td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={entity.status}
                            recordId={entity.id}
                            options={STATUS_OPTIONS}
                            onChange={handleStatusChange}
                            title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(entity)}
                          onEdit={() => handleEdit(entity)}
                          onDelete={() => handleDelete(entity.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Award size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total', { count: filteredEntities.length })}</span>
                      <span>{p('footer_marques')}: <span className="text-white font-semibold">{filteredEntities.filter(e => e.entity_type === 'marque').length}</span></span>
                      <span>{p('footer_fabricants')}: <span className="text-white font-semibold">{filteredEntities.filter(e => e.entity_type === 'fabricant').length}</span></span>
                      <span>{p('footer_linked_products')}: <span className="text-white font-semibold">{filteredEntities.reduce((s, e) => s + (parseInt(e.product_count, 10) || 0), 0)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedEntities.length > 0 ? (
              displayedEntities.map((entity, index) => (
                <motion.div
                  key={entity.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Award size={20} className="text-emerald-400" />
                          {entity.name || 'N/A'}
                        </h3>
                        {getTypeBadge(entity.entity_type)}
                        <QawaemClickableStatusBadge
                          status={entity.status}
                          recordId={entity.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80 font-mono">
                          {entity.entity_number}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_general')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_number_short')}:</span> {entity.entity_number || '—'}</div>
                            <div><span className="font-medium">{p('col_type')}:</span> {entity.entity_type === 'fabricant' ? p('type_fabricant') : p('type_marque')}</div>
                            <div><span className="font-medium">{p('label_created')}:</span> {formatDate(entity.created_at)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_location')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><MapPin size={14} className="text-amber-400" /><span className="font-medium">{p('col_country')}:</span> {entity.country || '—'}</div>
                            {entity.website && (
                              <div className="flex items-center gap-2"><Globe size={14} className="text-blue-400" /><span className="font-medium">{p('card_site')}:</span> {entity.website}</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_contact')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {entity.contact_email && <div className="flex items-center gap-2"><Mail size={14} className="text-blue-400" /><span>{entity.contact_email}</span></div>}
                            {entity.contact_phone && <div className="flex items-center gap-2"><Phone size={14} className="text-green-400" /><span>{entity.contact_phone}</span></div>}
                            {!entity.contact_email && !entity.contact_phone && <p className="text-white/40 text-xs italic">{p('no_contact')}</p>}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_products_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('label_linked_products')}:</span>
                              <p className="text-white font-bold text-lg mt-1">{entity.product_count || 0}</p>
                            </div>
                            {entity.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <p className="text-white/60 text-xs">{entity.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(entity)}
                      onEdit={() => handleEdit(entity)}
                      onDelete={() => handleDelete(entity.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Award size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Add/Edit Modal — 4 collapsible sections */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Award className="h-6 w-6 text-blue-400" />
                {selectedRecord ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="marques-fabricants-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedRecord ? p('update') : t('common.create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>

            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={selectedRecord ? p('modal_edit') : p('modal_new')}>
                {formTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = formTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                          : 'text-white/55 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <TabIcon size={16} className={active ? (tab.id === 'location_contact' ? 'text-blue-400' : tab.id === 'products_notes' ? 'text-amber-400' : 'text-emerald-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="marques-fabricants-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_entity_number')}</label>
                      <input
                        type="text"
                        value={formData.entity_number}
                        onChange={(e) => setFormData({ ...formData, entity_number: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                        placeholder={p('placeholder_entity_number')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('col_name')} required />
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_name')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_type')}</label>
                      <CustomSelect
                        value={formData.entity_type || ''}
                        onChange={(val) => setFormData({ ...formData, entity_type: val || '' })}
                        options={[
                          { value: 'marque', label: p('type_marque') },
                          { value: 'fabricant', label: p('type_fabricant') },
                        ]}
                        placeholder={p('placeholder_type')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_country')}</label>
                      <CustomSelect
                        value={formData.country || ''}
                        onChange={(val) => setFormData({ ...formData, country: val || '' })}
                        options={countryOptions}
                        placeholder={p('placeholder_country')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_website')}</label>
                      <input
                        type="text"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_website')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.status')}</label>
                      <CustomSelect
                        value={formData.status || ''}
                        onChange={(val) => setFormData({ ...formData, status: val || '' })}
                        options={STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
                        placeholder={p('placeholder_status')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.email')}</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_email')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.phone')}</label>
                      <input
                        type="text"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_phone')}
                        dir="ltr"
                      />
                    </div>
                  </div>
                )}

                {formTab === 'products_notes' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_linked_products')}</label>
                      <CustomNumberInput
                        step={1}
                        value={formData.product_count}
                        onChange={(v) => setFormData({ ...formData, product_count: v })}
                        placeholder="0"
                      />
                    </div>
                    <div className="min-w-0 md:col-span-2">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_notes')}
                      />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Award className="h-6 w-6 text-blue-400" />
                {p('view_details', { name: selectedRecord.name })}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors"><X size={24} /></button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Award size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_general')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_entity_number')}</p><p className="text-white font-semibold font-mono">{selectedRecord.entity_number || 'N/A'}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_name')}</p><p className="text-white font-semibold">{selectedRecord.name || 'N/A'}</p></div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_type')}</p>{getTypeBadge(selectedRecord.entity_type)}</div>
                  <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{t('common.status')}</p>{getStatusBadge(selectedRecord.status)}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Globe size={18} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_location')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_country')}</p><p className="text-white font-semibold">{selectedRecord.country || '—'}</p></div>
                    <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_website')}</p><p className="text-white font-semibold">{selectedRecord.website || '—'}</p></div>
                    <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_created_at')}</p><p className="text-white font-semibold">{formatDate(selectedRecord.created_at)} {formatTime(selectedRecord.created_at)}</p></div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Phone size={18} className="text-green-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_contact')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{t('common.email')}</p><p className="text-white font-semibold">{selectedRecord.contact_email || '—'}</p></div>
                    <div><p className="text-white/60 text-xs uppercase tracking-wider mb-1">{t('common.phone')}</p><p className="text-white font-semibold">{selectedRecord.contact_phone || '—'}</p></div>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Package size={18} className="text-amber-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_products_notes')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_linked_products')}</p>
                    <p className="text-white font-bold text-2xl">{selectedRecord.product_count || 0}</p>
                  </div>
                  {selectedRecord.notes && (
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_notes')}</p>
                      <p className="text-white/70 text-sm italic">{selectedRecord.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowViewModal(false)}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default MarquesFabricantsPage;
